import { Delete, Visibility as VisibilityIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  useTheme,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import { tokens } from '../../theme';

const FormsProducer = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('accessToken');
  const [render, setRender] = useState(false);

  const [producers, setProducers] = useState([]);

  useEffect(() => {
    const fetchProducers = async () => {
      try {
        const response = await axios.get(
          'https://api.opencurtainscasting.com/producer/getAll',
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const producerData = response.data.users.producerProfiles.map(
          (profile) => ({
            ...profile.user,
            imageURL: profile.profile.imageURL,
            id: profile.user.id,
          })
        );
        setProducers(producerData);
      } catch (error) {
        console.error('Error fetching producers:', error);
      }
    };
    fetchProducers();
  }, [token, render]);
  const DeleteUserProducer = async (userId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Are you sure want to delete this Actor`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, continue',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `https://api.opencurtainscasting.com/producer/deleteProducer/${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setRender(!render);
        Swal.fire('Success', 'User status updated successfully', 'success');
      } catch (error) {
        Swal.fire(
          'Error',
          'There was an error updating the user status',
          'error'
        );
      }
    } else {
      Swal.fire('Canceled', 'The action has been canceled.', 'info');
    }
  };

  const handleAdminActiveChange = async (userId, newStatus) => {
    const activateStatus = newStatus ? 1 : 0;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${
        newStatus ? 'activate' : 'deactivate'
      } this user.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, continue',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await axios.patch(
          `https://api.opencurtainscasting.com/user/updateUserAccess/${userId}`,
          { activate: activateStatus },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setProducers((prevProducers) =>
          prevProducers.map((producer) =>
            producer.id === userId
              ? { ...producer, adminActive: activateStatus === 1 }
              : producer
          )
        );

        Swal.fire('Success', 'User status updated successfully', 'success');
      } catch (error) {
        Swal.fire(
          'Error',
          'There was an error updating the user status',
          'error'
        );
      }
    } else {
      Swal.fire('Canceled', 'The action has been canceled.', 'info');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.3 },
    { field: 'firstName', headerName: 'First Name', flex: 1 },
    { field: 'lastName', headerName: 'Last Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1.3 },
    { field: 'contactNumber', headerName: 'Contact Number', flex: 1 },
    {
      field: 'adminActive',
      headerName: 'Admin Active',
      flex: 1,
      renderCell: ({ row }) => (
        <Select
          value={row.adminActive ? 'Active' : 'Deactivate'}
          onChange={(event) =>
            handleAdminActiveChange(row.id, event.target.value === 'Active')
          }
          displayEmpty
          variant='standard'
          sx={{
            fontWeight: 'bold',
            color: row.adminActive ? '#6e0506' : colors.redAccent[500],
          }}
        >
          <MenuItem value='Active'>Active</MenuItem>
          <MenuItem value='Deactivate'>Deactivate</MenuItem>
        </Select>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title='View'>
            <Link to={`/producer/${row.id}`}>
              <IconButton sx={{ color: '#6e0506', borderRadius: '4px' }}>
                <VisibilityIcon />
              </IconButton>
            </Link>
          </Tooltip>
          <Tooltip title='Delete' sx={{ outline: 'none' }}>
            <IconButton
              sx={{ color: '#6e0506', borderRadius: '4px' }}
              onClick={() => DeleteUserProducer(row?.id)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box color={'#6e0506'} m='20px'>
      <Header title='PRODUCERS' subtitle='Manage Producers' />
      <Box
        m='40px 0 0 0'
        height='70vh'
        sx={{
          '& .MuiDataGrid-root': {
            border: 'none',
            fontSize: '14px',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
            color: '#6e0506',
          },
          '& .name-column--cell': {
            color: colors.greenAccent[300],
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgba(112, 14, 16, 0.2)',
            borderTopLeftRadius: '5px',
            borderTopRightRadius: '5px',
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: 'none',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: 'rgba(112, 14, 16, 0.2)',
            borderBottomLeftRadius: '5px',
            borderBottomRightRadius: '5px',
          },
          '& .MuiCheckbox-root': {
            color: `${colors.greenAccent[200]} !important`,
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255, 206, 207, 0.5)',
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(112, 14, 16, 0.3)',
            borderRadius: '4px',
          },
          '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(112, 14, 16, 0.5)',
          },
        }}
      >
        <DataGrid
          rows={producers}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          getRowId={(row) => row.id}
          sx={{ color: '#6e0506' }}
        />
      </Box>
    </Box>
  );
};

export default FormsProducer;
